import { Component, OnInit } from '@angular/core';
import { ChartConfiguration, ChartData, ChartType, ChartEvent } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { InvoiceApi } from '@app/services/invoice-api';
import { InvoiceInterface } from '@app/interface/invoice';
import { finalize } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AfterViewInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ChangeDetectorRef } from '@angular/core';
import { RecursiveInterface } from '@app/services/base-authenticated.service';
import { ActivatedRoute, Router } from '@angular/router'; // Add Router import
import { Input, OnChanges, SimpleChanges } from '@angular/core';

@Component({
  selector: 'app-pie-chart',
  imports: [
    CommonModule,
    MatCardModule,
    BaseChartDirective,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
  templateUrl: './pie-chart.component.html',
  styleUrl: './pie-chart.component.scss',
})
export class PieChartComponent implements OnInit {
  @Input() selectedYear: number = new Date().getFullYear();

  public pieChartData: ChartData<'pie'> = {
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
        hoverBackgroundColor: [],
      },
    ],
  };

  public pieChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#424242',
          font: { weight: 'normal' },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: '#616161',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        padding: 12,
        cornerRadius: 12,
        displayColors: true,
        callbacks: {
          label: (tooltipItem: any) => {
            const label = tooltipItem.label || '';
            const value = tooltipItem.raw;
            const dataset = tooltipItem.dataset.data;

            // Calculate total only from numeric values
            const total = dataset.reduce((sum: number, val: any) => {
              return sum + (typeof val === 'number' ? val : 0);
            }, 0);

            const percentage =
              total > 0 ? Math.round((value / total) * 100) : 0;

            // Return array to show multiple lines in tooltip
            return [
              `${label}`,
              `Totale: €${value.toLocaleString('it-It', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
              `Quota sul totale: ${percentage}%`,
            ];
          },
        },
      },
    },
    // Add click event handler
    onClick: (event: ChartEvent, elements: any[], chart: any) => {
      this.handleChartClick(event, elements, chart);
    },
  };

  public pieChartType: ChartType = 'pie';
  public isLoading = true;
  public errorMessage: string | null = null;
  public totalInvoices = 0;

  // Store company information for click handling
  private companyData: {
    companyId: number;
    companyName: string;
    total: number;
  }[] = [];

  getCssVariable(name: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
  }

  // Color palette for companies (extend as needed)
  private colorPalette = [
    this.getCssVariable('--primary-color'),
    this.getCssVariable('--accent-color'),
    this.getCssVariable('--warn-2-color'),
    this.getCssVariable('--info-color'),
    '#fda075',
    '#009d7a',
    '#7864fb',
    '#c5e9dd',
    '#eef0afff',
    '#8dd2fc',
    '#5c7a85',
    '#00a1c1',
  ];

  constructor(
    private invoiceApi: InvoiceApi,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadInvoiceData();

    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When selectedYear changes, reload the data
    if (changes['selectedYear'] && !changes['selectedYear'].firstChange) {
      this.loadInvoiceData();
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);

    this.cdr.detectChanges();
  }

  loadInvoiceData(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.isLoading = true;
    this.errorMessage = null;

    this.cdr.detectChanges();

    // Get invoices for the current year
    const currentYear = new Date().getFullYear();
    const params = {
      emission_date__gte: `${this.selectedYear}-01-01`,
      emission_date__lte: `${this.selectedYear}-12-31`,
      is_draft: false,
      is_preventive: false,
      is_deleted: false,
      is_saved_for_preview: false,
    };

    // Get all invoices (you might want to add date filters)
    this.invoiceApi
      .getAllInvoices<InvoiceInterface>(organization_id, params)
      // .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: RecursiveInterface<InvoiceInterface>) => {
          (this.processCompanyData(response.results), (this.isLoading = false));

          this.isLoading = false;

          this.cdr.detectChanges(); // Force change detection after data
        },
        error: (err: any) => {
          console.error('Failed to load invoices:', err);
          // this.errorMessage = 'Errore nel caricamento delle fatture. Riprova più tardi.';

          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });

    this.cdr.detectChanges();
  }

  private processCompanyData(invoices: InvoiceInterface[]): void {
    if (!invoices || invoices.length === 0) {
      this.errorMessage = "Nessun documento per l'anno selezionato.";
      this.pieChartData = {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [],
            hoverBackgroundColor: [],
          },
        ],
      };
      return;
    }

    // Group invoices by company_id and store company info
    const companyTotals: {
      [key: number]: {
        total: number;
        companyName: string;
        companyId: number;
      };
    } = {};

    invoices.forEach((invoice) => {
      const companyId = invoice.company_id || 0;
      const companyName =
        invoice.company?.company_name || `Company ${companyId}`;

      const amount = Number(invoice.amount) || 0;

      if (!companyTotals[companyId]) {
        companyTotals[companyId] = {
          total: 0,
          companyName: companyName,
          companyId: companyId,
        };
      }

      // Filter invoices to count only non-draft and non-preventive
      // console.log('1', invoice);
      const validInvoices = invoices.filter(
        (invoice) =>
          invoice.is_draft === false &&
          invoice.is_preventive === false &&
          invoice.is_deleted == false,
      );

      if (invoice.document_type === 'TD04') {
        companyTotals[companyId].total -= Number(invoice.amount) || 0; // subtract if Nota di credito
      } else {
        companyTotals[companyId].total += Number(invoice.amount) || 0;
      }
    });

    // Sort companies by total amount (descending)
    const sortedCompanies = Object.values(companyTotals)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10); // Limit to top 10 companies

    // Store company data for click handling
    this.companyData = sortedCompanies;

    // Prepare chart data
    const labels = sortedCompanies.map((company) => company.companyName);
    const data = sortedCompanies.map((company) => company.total);
    const backgroundColors = labels.map(
      (_, index) => this.colorPalette[index % this.colorPalette.length],
    );

    this.totalInvoices = invoices.length;
    this.pieChartData = {
      labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors,
          hoverBackgroundColor: backgroundColors.map((color) =>
            this.lightenColor(color, 20),
          ),
          borderWidth: 1,
        },
      ],
    };
  }

  // Handle chart click events
  private handleChartClick(
    event: ChartEvent,
    elements: any[],
    chart: any,
  ): void {
    if (elements.length > 0) {
      const firstElement = elements[0];
      const datasetIndex = firstElement.datasetIndex;
      const index = firstElement.index;

      if (this.companyData[index]) {
        const company = this.companyData[index];
        this.navigateToCompanyDetails(company.companyId, company.companyName);
      }
    }
  }

  // Navigate to company details page
  private navigateToCompanyDetails(
    companyId: number,
    companyName: string,
  ): void {
    const organizationId = this.route.snapshot.paramMap.get('organizationId');

    // console.log(organizationId);

    // Example 1: Navigate to a company details page
    this.router.navigate([`/${organizationId}/company/${companyId}`]);

    // Example 2: Navigate to invoices page filtered by this company
    // this.router.navigate([`/organization/${organizationId}/invoices`], {
    //   queryParams: { company_id: companyId }
    // });

    // Example 3: Navigate to a generic company page
    // this.router.navigate([`/organization/${organizationId}/companies`, companyId]);

    // console.log(`Clicked on company: ${companyName} (ID: ${companyId})`);
  }

  // Helper function to lighten colors for hover effect
  private lightenColor(color: string, percent: number): string {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;

    return `#${(
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)}`;
  }

  refreshData(): void {
    this.loadInvoiceData();
  }
}
